import React from "react";
import { Link } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";
import { Mail, ShieldCheck } from "lucide-react";
import Seo from "../components/seo";
import { currentLanguage } from "../utils/locale";

const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/contact`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;

const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/icons/logo.webp`,
        width: 512,
        height: 512,
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        url: pageUrl,
        availableLanguage: "English",
      },
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: "en",
    },
    {
      "@type": "ContactPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Contact TarotFate",
      description:
        "Contact TarotFate for site questions, corrections, privacy requests and general feedback.",
      isPartOf: { "@id": `${siteUrl}#website` },
      inLanguage: "en",
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Contact", item: pageUrl },
      ],
    },
  ],
};

export default function Contact() {
  const isSpanish = currentLanguage() === "es";
  const copy = isSpanish
    ? {
        seoTitle: "Contacto | TarotFate",
        description:
          "Contacta a TarotFate para preguntas del sitio, correcciones, solicitudes de privacidad y comentarios generales.",
        title: "Contacto",
        intro:
          "Usa esta pagina para preguntas del sitio, correcciones, solicitudes de privacidad, notas de accesibilidad o comentarios generales sobre TarotFate.",
        trustTitle: "Confianza editorial",
        trustSubtitle: "Politicas claras y una forma directa de contacto.",
        trust1:
          "TarotFate es un recurso reflexivo de tarot. Las lecturas son para insight personal y no reemplazan orientacion medica, legal o financiera profesional.",
        trust2: "No incluyas informacion personal sensible en tus mensajes. Los detalles estan en la politica de privacidad.",
        trust3: "Para el proposito, limites y uso responsable del sitio, consulta el aviso legal.",
        privacy: "Politica de privacidad",
        disclaimer: "Aviso legal",
        formTitle: "Enviar un mensaje",
        formNote: "Los campos requeridos estan marcados en el formulario.",
        success: "Gracias. Tu mensaje fue enviado.",
        name: "Nombre",
        email: "Email",
        topic: "Tema",
        topics: ["Comentario general", "Solicitud de correccion", "Solicitud de privacidad", "Nota de accesibilidad", "Pregunta del sitio"],
        message: "Mensaje",
        sending: "Enviando",
        submit: "Enviar mensaje",
      }
    : {
        seoTitle: "Contact TarotFate | Questions, Corrections & Privacy Requests",
        description: "Contact TarotFate for site questions, corrections, privacy requests and general feedback.",
        title: "Contact",
        intro:
          "Use this page for site questions, corrections, privacy requests, accessibility notes or general feedback about TarotFate.",
        trustTitle: "Publisher Trust",
        trustSubtitle: "Clear policies and reachable contact.",
        trust1:
          "TarotFate is a reflective tarot resource. Readings are for personal insight and do not replace professional medical, legal or financial guidance.",
        trust2: "Please do not include sensitive personal information in messages. Privacy details are available on the",
        trust3: "For site purpose, limitations and responsible use, see the",
        privacy: "Privacy Policy",
        disclaimer: "Disclaimer",
        formTitle: "Send a Message",
        formNote: "Required fields are marked in the form.",
        success: "Thank you. Your message has been sent.",
        name: "Name",
        email: "Email",
        topic: "Topic",
        topics: ["General feedback", "Correction request", "Privacy request", "Accessibility note", "Site question"],
        message: "Message",
        sending: "Sending",
        submit: "Send Message",
      };
  const [formState, handleSubmit] = useForm("xvgqwqll");

  return (
    <main className="font-[Cormorant_Garamond] text-white min-h-[100svh] relative overflow-hidden">
      <Seo
        title={copy.seoTitle}
        description={copy.description}
        url={isSpanish ? `${siteUrl}/es/contact` : pageUrl}
        image={ogImage}
        schema={isSpanish ? undefined : schemaGraph}
      />

      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(1100px 760px at 50% -12%, rgba(57,195,212,0.25), rgba(57,195,212,0) 42%), linear-gradient(180deg, #014C75 0%, #005C83 38%, #0C4B84 72%, #821E3C 100%)",
        }}
      />

      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="text-center">
          <p className="text-[12px] font-semibold uppercase tracking-[0.26em] text-white/68">
            TarotFate
          </p>
          <h1 className="mt-3 text-3xl font-semibold sm:text-4xl">
            {copy.title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[18px] leading-relaxed text-white/84">
            {copy.intro}
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.35fr]">
          <aside className="rounded-md border border-white/22 bg-white/8 p-6 text-left shadow-[0_18px_45px_rgba(0,0,0,0.24)]">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-md border border-white/25 bg-white/10">
                <ShieldCheck size={20} />
              </span>
              <div>
                <h2 className="text-xl font-semibold">{copy.trustTitle}</h2>
                <p className="text-sm text-white/68">{copy.trustSubtitle}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-white/78">
              <p>
                {copy.trust1}
              </p>
              <p>
                {copy.trust2}{" "}
                <Link to={isSpanish ? "/es/privacy" : "/privacy"} className="text-white underline underline-offset-4">
                  {copy.privacy}
                </Link>
                .
              </p>
              <p>
                {copy.trust3}{" "}
                <Link to={isSpanish ? "/es/disclaimer" : "/disclaimer"} className="text-white underline underline-offset-4">
                  {copy.disclaimer}
                </Link>
                .
              </p>
            </div>
          </aside>

          <section className="rounded-md border border-white/22 bg-white/92 p-6 text-left text-[#12344b] shadow-[0_18px_45px_rgba(0,0,0,0.24)]">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#014C75]/10 text-[#014C75]">
                <Mail size={20} />
              </span>
              <div>
                <h2 className="text-2xl font-semibold">{copy.formTitle}</h2>
                <p className="text-sm text-[#12344b]/68">
                  {copy.formNote}
                </p>
              </div>
            </div>

            {formState.succeeded ? (
              <div className="mt-6 rounded-md border border-[#0f6f65]/25 bg-[#0f6f65]/10 px-4 py-3 text-[#0f4c46]">
                {copy.success}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-semibold">
                    {copy.name}
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="mt-1 w-full rounded-md border border-[#12344b]/20 bg-white px-3 py-2 text-base outline-none transition focus:border-[#014C75] focus:ring-2 focus:ring-[#014C75]/20"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold">
                    {copy.email}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="mt-1 w-full rounded-md border border-[#12344b]/20 bg-white px-3 py-2 text-base outline-none transition focus:border-[#014C75] focus:ring-2 focus:ring-[#014C75]/20"
                  />
                  <ValidationError prefix="Email" field="email" errors={formState.errors} />
                </div>

                <div>
                  <label htmlFor="contact-topic" className="block text-sm font-semibold">
                    {copy.topic}
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    className="mt-1 w-full rounded-md border border-[#12344b]/20 bg-white px-3 py-2 text-base outline-none transition focus:border-[#014C75] focus:ring-2 focus:ring-[#014C75]/20"
                    defaultValue={copy.topics[0]}
                  >
                    {copy.topics.map((topic) => <option key={topic}>{topic}</option>)}
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-semibold">
                    {copy.message}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={6}
                    className="mt-1 w-full resize-y rounded-md border border-[#12344b]/20 bg-white px-3 py-2 text-base outline-none transition focus:border-[#014C75] focus:ring-2 focus:ring-[#014C75]/20"
                  />
                  <ValidationError prefix="Message" field="message" errors={formState.errors} />
                </div>

                <button
                  type="submit"
                  disabled={formState.submitting}
                  className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#014C75] px-5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-[#0C4B84] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {formState.submitting ? copy.sending : copy.submit}
                </button>
              </form>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}
